import { useState, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { useParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Alert,
  CircularProgress,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Stack,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { managerApi, type ManagerDwollaInfo } from '../../api/manager_api';
import { useManagerUser } from './ManagerUserPage';

export const ManagerUserDwolla = observer(() => {
  const { userKey } = useParams<{ userKey: string }>();
  const { user, error: userError } = useManagerUser();

  const [dwollaInfo, setDwollaInfo] = useState<ManagerDwollaInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchDwollaInfo = async () => {
      if (!userKey) return;

      setLoading(true);
      setError(null);

      try {
        const result = await managerApi.getDwollaInfo(userKey);
        setDwollaInfo(result);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load Dwolla info');
      } finally {
        setLoading(false);
      }
    };

    fetchDwollaInfo();
  }, [userKey]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const formatCurrency = (amount: string, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD',
    }).format(parseFloat(amount));
  };

  if (!user) {
    return null;
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      {(error || userError) && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error || userError}
        </Alert>
      )}

      {/* Status Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Dwolla Status
          </Typography>
          <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
            <Chip
              icon={dwollaInfo?.has_customer ? <CheckCircleIcon /> : <CancelIcon />}
              label={dwollaInfo?.has_customer ? 'Customer Created' : 'Customer Not Created'}
              color={dwollaInfo?.has_customer ? 'success' : 'default'}
              variant={dwollaInfo?.has_customer ? 'filled' : 'outlined'}
            />
            <Chip
              icon={dwollaInfo?.has_funding_source ? <CheckCircleIcon /> : <CancelIcon />}
              label={dwollaInfo?.has_funding_source ? 'Bank Account Linked' : 'No Bank Account Linked'}
              color={dwollaInfo?.has_funding_source ? 'success' : 'default'}
              variant={dwollaInfo?.has_funding_source ? 'filled' : 'outlined'}
            />
          </Stack>

          {/* Raw URLs for debugging */}
          {dwollaInfo?.dwolla_customer_location && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              <strong>Customer URL:</strong> {dwollaInfo.dwolla_customer_location}
            </Typography>
          )}
          {dwollaInfo?.dwolla_funding_location && (
            <Typography variant="body2" color="text.secondary">
              <strong>Funding URL:</strong> {dwollaInfo.dwolla_funding_location}
            </Typography>
          )}
          {!dwollaInfo?.dwolla_customer_location && (
            <Typography variant="body2" color="text.secondary">
              No Dwolla account set up for this user.
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Bank Accounts Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Bank Accounts
          </Typography>
          {!dwollaInfo?.sources.length ? (
            <Typography variant="body2" color="text.secondary">
              No bank accounts linked.
            </Typography>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Bank</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Created</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {dwollaInfo.sources.map((source) => (
                    <TableRow key={source.id}>
                      <TableCell>{source.name}</TableCell>
                      <TableCell>{source.bank_name || '-'}</TableCell>
                      <TableCell>
                        {source.bank_account_type || source.type}
                      </TableCell>
                      <TableCell>{formatDate(source.created)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Transaction History Card */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Transaction History
          </Typography>
          {!dwollaInfo?.transactions.length ? (
            <Typography variant="body2" color="text.secondary">
              No transactions found.
            </Typography>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Amount</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {dwollaInfo.transactions.map((transaction) => (
                    <TableRow key={transaction.id}>
                      <TableCell>{formatDate(transaction.created)}</TableCell>
                      <TableCell>
                        {formatCurrency(transaction.amount, transaction.currency)}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={transaction.status}
                          size="small"
                          color={
                            transaction.status === 'processed'
                              ? 'success'
                              : transaction.status === 'pending'
                              ? 'warning'
                              : transaction.status === 'failed'
                              ? 'error'
                              : 'default'
                          }
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </>
  );
});
